import { useCallback, useEffect, useState } from 'react';
import { Loader, AlertCircle, Ticket, Plus, Unlink, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventTicketLink } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { cn } from '../../utils/cn';
import { TicketIdLink } from '../TicketIdLink';

export interface EventTicketsSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/** Discord ticket links (guide §1.4) with per-ticket reverse lookup. */
export const EventTicketsSection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventTicketsSectionProps) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<EventTicketLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [newTicketId, setNewTicketId] = useState('');
  const [adding, setAdding] = useState(false);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reverse, setReverse] = useState<EventTicketLink[] | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.listTicketLinks(targetType, targetId);
      setTickets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleAdd = async () => {
    const ticketId = Number(newTicketId.trim());
    if (!Number.isInteger(ticketId) || ticketId <= 0) {
      alert('Enter a valid numeric ticket id.');
      return;
    }
    setAdding(true);
    try {
      await eventTeamApiService.linkTicket(targetType, targetId, ticketId);
      setNewTicketId('');
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link ticket');
    } finally {
      setAdding(false);
    }
  };

  const handleUnlink = async (ticketId: number) => {
    if (!confirm(`Unlink ticket #${ticketId}?`)) return;
    setBusyId(ticketId);
    try {
      await eventTeamApiService.unlinkTicket(targetType, targetId, ticketId);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink ticket');
    } finally {
      setBusyId(null);
    }
  };

  const toggleReverse = async (ticketId: number) => {
    if (expandedId === ticketId) {
      setExpandedId(null);
      setReverse(null);
      return;
    }
    setExpandedId(ticketId);
    setReverse(null);
    setReverseLoading(true);
    try {
      const results = await eventTeamApiService.getTargetsForTicket(ticketId);
      setReverse(results);
    } catch {
      setReverse([]);
    } finally {
      setReverseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
        <Ticket className="w-4 h-4 text-pink-400" />
        Tickets ({tickets.length})
      </h3>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {perms.canCreate && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={newTicketId}
            onChange={(e) => setNewTicketId(e.target.value)}
            placeholder="Ticket id (e.g. 56657)"
            className="glass-input text-sm w-48"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAdd();
            }}
          />
          <button
            onClick={() => void handleAdd()}
            disabled={adding || !newTicketId.trim()}
            className="glass-button text-xs inline-flex items-center gap-1.5"
          >
            {adding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Link ticket
          </button>
        </div>
      )}

      {tickets.length === 0 && !error ? (
        <p className="text-glass-400 text-sm py-4 text-center">No tickets linked.</p>
      ) : (
        <div className="space-y-1.5">
          {tickets.map((ticket) => {
            const busy = busyId === ticket.ticket_id;
            const expanded = expandedId === ticket.ticket_id;
            return (
              <div key={ticket.ticket_id} className="rounded-lg bg-glass-100/5 border border-glass-200/10">
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-sm text-white font-mono inline-flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-pink-400" />
                    <TicketIdLink ticketId={ticket.ticket_id} className="text-white hover:text-accent-300 font-mono">
                      #{ticket.ticket_id}
                    </TicketIdLink>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => void toggleReverse(ticket.ticket_id)}
                      className="glass-button-secondary !p-1.5"
                      title="Show other events/projects linked to this ticket"
                    >
                      {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    {perms.canCreate && (
                      <button
                        onClick={() => void handleUnlink(ticket.ticket_id)}
                        disabled={busy}
                        className="glass-button-secondary !p-1.5 text-red-300"
                        title="Unlink ticket"
                      >
                        {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="px-3 pb-2 border-t border-glass-200/10 pt-2">
                    <p className="text-[11px] text-glass-500 mb-1">
                      Also linked to ticket{' '}
                      <TicketIdLink ticketId={ticket.ticket_id} className="text-glass-400 hover:text-accent-300 font-mono">
                        #{ticket.ticket_id}
                      </TicketIdLink>
                      :
                    </p>
                    {reverseLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-glass-400" />
                    ) : !reverse ||
                      reverse.filter((r) => r.item_id !== targetId).length === 0 ? (
                      <p className="text-xs text-glass-500">Nothing else links to this ticket.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {reverse
                          .filter((r) => r.item_id !== targetId)
                          .map((r) => (
                            <button
                              key={`${r.item_type}-${r.item_id}`}
                              onClick={() => {
                                if (r.item_type === 'event') navigate(`/event-team/events/${r.item_id}`);
                                else if (r.item_type === 'event_project') navigate(`/event-team/projects/${r.item_id}`);
                              }}
                              className={cn(
                                'text-[11px] px-2 py-0.5 rounded-full border text-glass-300 border-glass-200/20',
                                (r.item_type === 'event' || r.item_type === 'event_project') &&
                                  'hover:text-white hover:border-glass-200/40 transition-colors',
                              )}
                            >
                              {r.item_type === 'event' ? '🎉' : r.item_type === 'event_project' ? '📁' : '🔗'}{' '}
                              {r.item_type ?? 'item'}: {r.item_id}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
